import { useState, useEffect } from 'react';
import {
  Avatar, Box, Button, Card, CardContent, CardHeader,
  Dialog, DialogActions, DialogContent, MenuItem,
  DialogTitle, Divider, Grid, IconButton, TextField, Typography,
  Snackbar, Alert, Paper, Stack, Chip
} from '@mui/material';
import {
  Edit as EditIcon, CameraAlt as CameraIcon, Person as PersonIcon,
  Lock as LockIcon, Email as EmailIcon,
  Phone as PhoneIcon, Home as HomeIcon, Wc as GenderIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/studentAuthContext';
import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BASE_URL ||
  'http://localhost:5000';

const parsePreference = (rawPreference) => {
  if (!rawPreference) return [];

  if (Array.isArray(rawPreference)) {
    return rawPreference.map((item) => String(item).trim()).filter(Boolean);
  }

  if (typeof rawPreference === 'string') {
    const trimmed = rawPreference.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch (_error) {
      return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
    }

    return [trimmed];
  }

  return [];
};

const Profile = () => {
  const { student, setStudent } = useAuth();
  const [avatar, setAvatar] = useState(null);
  const [preview, setPreview] = useState('');
  const [formData, setFormData] = useState({
    name: '', email: '', phone_number: '', address: '', gender: '', preference: [],
  });

  const [openEditModal, setOpenEditModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [preferenceOptions, setPreferenceOptions] = useState([]);
  const [passwordForm, setPasswordForm] = useState({ 
    currentPassword: '', 
    newPassword: '', 
    confirmPassword: '' 
  });
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

  // FETCH PROFILE DATA
  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/students/preference-options`)
      .then((res) => {
        const options = (res.data?.data || []).map((row) => row.name).filter(Boolean);
        setPreferenceOptions(options);
      })
      .catch(() => {
        setPreferenceOptions([]);
        showSnackbar('Could not load preference options from backend.', 'warning');
      });
  }, []);

  useEffect(() => {
    if (student?.id) {
      axios.get(`${API_BASE_URL}/api/students/profile/${student.id}`)
        .then(res => {
          const data = res.data;
          setFormData({
            name: data.name,
            email: data.email,
            phone_number: data.phone_number || '',
            address: data.address || '',
            gender: data.gender || '',
            preference: parsePreference(data.preference),
          });
          setPreview(data.profile_img ? `${API_BASE_URL}/uploads/students/${data.profile_img}` : '');
        })
        .catch(() => showSnackbar("Failed to fetch profile", "error"));
    }
  }, [student]);

  // HANDLERS
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatar(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePreferenceChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      preference: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  // SUBMIT PROFILE UPDATE
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();
    Object.entries(formData).forEach(([key, val]) => {
      if (key === 'preference') {
        form.append(key, JSON.stringify(val || []));
        return;
      }
      form.append(key, val ?? '');
    });
    if (avatar) form.append('profile_img', avatar);

    try {
      const res = await axios.put(`${API_BASE_URL}/api/students/profile/${student.id}`, form);
      const updatedData = res.data?.data;
      if (updatedData && setStudent) {
        setStudent({
          ...student,
          name: updatedData.name || formData.name,
          profile_img: updatedData.profile_img || student.profile_img,
        });
      }
      showSnackbar("Profile updated successfully", "success");
      setOpenEditModal(false);
    } catch (err) {
      showSnackbar("Profile update failed", "error");
    }
  };

  // SUBMIT PASSWORD CHANGE
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (newPassword !== confirmPassword) {
      return showSnackbar("Passwords do not match", "error");
    }

    try {
      await axios.put(`${API_BASE_URL}/api/students/change-password/${student.id}`, {
        currentPassword, newPassword
      });
      showSnackbar("Password changed successfully", "success");
      setOpenPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Password change failed", "error");
    }
  };

  const InfoTile = ({ icon, label, value }) => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2, border: '1px solid #e0e0e0' }}>
      <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase' }}>{label}</Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>{value || 'Not Provided'}</Typography>
      </Box>
    </Paper>
  );

  return (

    <Box sx={{ p: { xs: 2, md: 4 }, mt: 10, maxWidth: '1200px', mx: 'auto' }}>
      <Grid container spacing={4}>
        {/* Left Column: Avatar & Quick Actions */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ borderRadius: 4, textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
            <Box sx={{ height: 100, background: 'linear-gradient(45deg, #1976d2, #42a5f5)' }} />
            <CardContent sx={{ mt: -8 }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={preview}
                  sx={{ width: 140, height: 140, border: '6px solid white', boxShadow: '0 4px 14px rgba(0,0,0,0.1)', mb: 2 }}
                >
                  <PersonIcon sx={{ fontSize: 60 }} />
                </Avatar>
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute', bottom: 15, right: 5,
                    bgcolor: 'white', color: 'primary.main',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <CameraIcon fontSize="small" />
                  <input type="file" hidden accept="image/*" onChange={handleAvatarChange} />
                </IconButton>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{formData.name}</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>Student Account</Typography>
              
              <Stack spacing={2}>
                <Button fullWidth variant="contained" startIcon={<EditIcon />} onClick={() => setOpenEditModal(true)} sx={{ borderRadius: 2, py: 1 }}>
                  Edit Profile
                </Button>
                <Button fullWidth variant="outlined" startIcon={<LockIcon />} onClick={() => setOpenPasswordModal(true)} sx={{ borderRadius: 2, py: 1 }}>
                  Security
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Detailed Info */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardHeader title={<Typography variant="h6" sx={{ fontWeight: 700 }}>Profile Details</Typography>} />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoTile icon={<EmailIcon />} label="Email Address" value={formData.email} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoTile icon={<PhoneIcon />} label="Phone Number" value={formData.phone_number} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoTile icon={<GenderIcon />} label="Gender" value={formData.gender} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoTile icon={<HomeIcon />} label="Address" value={formData.address} />
                </Grid>
                <Grid size={12}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, border: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', mb: 1 }}>
                      Course Preferences
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {formData.preference.length > 0 ? (
                        formData.preference.map((course) => (
                          <Chip key={course} label={course} color="primary" variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No preferences selected</Typography>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Profile Modal */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Personal Info</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label="Full Name" name="name" value={formData.name} onChange={handleFormChange} variant="filled" />
            <TextField select fullWidth label="Gender" name="gender" value={formData.gender} onChange={handleFormChange} variant="filled">
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
            </TextField>
            <TextField fullWidth label="Phone Number" name="phone_number" value={formData.phone_number} onChange={handleFormChange} variant="filled" />
            <TextField fullWidth label="Address" name="address" value={formData.address} onChange={handleFormChange} variant="filled" multiline rows={2} />
            <TextField
              select
              fullWidth
              label="Course Preferences"
              name="preference"
              value={formData.preference}
              onChange={handlePreferenceChange}
              variant="filled"
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Stack>
                ),
              }}
            >
              {preferenceOptions.length > 0 ? (
                preferenceOptions.map((course) => (
                  <MenuItem key={course} value={course}>{course}</MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No preference options available
                </MenuItem>
              )}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenEditModal(false)} color="inherit">Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" sx={{ borderRadius: 2, px: 4 }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={openPasswordModal} onClose={() => setOpenPasswordModal(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Security Settings</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth type="password" label="Current Password" name="currentPassword" value={passwordForm.currentPassword} onChange={handlePasswordFormChange} variant="filled" />
            <TextField fullWidth type="password" label="New Password" name="newPassword" value={passwordForm.newPassword} onChange={handlePasswordFormChange} variant="filled" />
            <TextField fullWidth type="password" label="Confirm Password" name="confirmPassword" value={passwordForm.confirmPassword} onChange={handlePasswordFormChange} variant="filled" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenPasswordModal(false)} color="inherit">Cancel</Button>
          <Button onClick={handlePasswordSubmit} variant="contained" sx={{ borderRadius: 2, px: 4 }}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
   
  );
};

export default Profile;